import { FormEvent, useState } from 'react';
import type { Quadrant as QuadrantKey, Task } from '../types';
import { QUADRANT_LABELS } from '../types';
import { TaskCard } from './TaskCard';
import { signOut } from '../lib/auth';

// A lapsed magic-link session shows up as one of these when we try to write.
function isAuthError(message: string): boolean {
  const m = message.toLowerCase();
  return (
    m.includes('not authenticated') ||
    m.includes('jwt') ||
    m.includes('expired') ||
    m.includes('401') ||
    m.includes('row-level security') ||
    m.includes('row level security')
  );
}

interface Props {
  quadrant: QuadrantKey;
  tasks: Task[];
  readOnly: boolean;
  onAdd: (quadrant: QuadrantKey, title: string) => Promise<void>;
  onUpdate: (id: string, patch: Partial<Pick<Task, 'title' | 'notes' | 'quadrant'>>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onMove: (id: string, to: QuadrantKey) => Promise<void>;
}

export function Quadrant({ quadrant, tasks, readOnly, onAdd, onUpdate, onDelete, onMove }: Props) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [authExpired, setAuthExpired] = useState(false);
  const label = QUADRANT_LABELS[quadrant];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = draft.trim();
    if (!trimmed) return;
    setBusy(true);
    setErrMsg(null);
    setAuthExpired(false);
    try {
      await onAdd(quadrant, trimmed);
      setDraft('');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      if (isAuthError(message)) {
        setAuthExpired(true);
        setErrMsg('Your session has expired. Please sign in again — your card was not saved.');
      } else {
        setErrMsg(`Could not add card: ${message}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`quadrant quadrant--${quadrant}`}>
      <header className="quadrant-header">
        <h2>{label.title}</h2>
        <span className="quadrant-subtitle">{label.subtitle}</span>
        <span className="quadrant-count">{tasks.length}</span>
      </header>

      <div className="quadrant-tasks">
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            readOnly={readOnly}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onMove={onMove}
          />
        ))}
        {tasks.length === 0 && (
          <div className="quadrant-empty">No tasks</div>
        )}
      </div>

      {!readOnly && (
        <form className="quadrant-add" onSubmit={submit}>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`Add to ${label.title.toLowerCase()}…`}
            disabled={busy}
          />
          <button type="submit" disabled={busy || !draft.trim()}>+</button>
        </form>
      )}

      {errMsg && (
        <div className="quadrant-add-error" role="alert">
          <span>{errMsg}</span>
          {authExpired && (
            <button type="button" onClick={() => signOut()}>Sign in again</button>
          )}
        </div>
      )}
    </section>
  );
}
